import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2, 
  Phone, 
  MessageCircle,
  MapPin,
  Calendar,
  Clock,
  Ruler,
  Award,
  Star,
  ChevronRight,
  Quote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LazyImage from "@/components/LazyImage";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

// Detailed project data with SEO content
const PROJECTS_DATA = {
  "renovation-appartement-bruxelles": {
    slug: "renovation-appartement-bruxelles",
    title: "Rénovation Appartement Bruxelles",
    subtitle: "Transformation complète d'un appartement de 120m²",
    metaTitle: "Rénovation Appartement Bruxelles 120m² | Perfect Team Renov",
    metaDescription: "Découvrez notre projet de rénovation complète d'un appartement à Bruxelles : cuisine ouverte, parquet, peinture. Avant/après et témoignage client.",
    category: "Rénovation Complète",
    location: "Bruxelles-Ville",
    year: "2024",
    duration: "8 semaines",
    surface: "120 m²",
    
    heroImage: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200",
    beforeImage: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200",
    afterImage: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200",
    
    description: `Ce projet de **rénovation complète** concernait un appartement de 120m² situé au cœur de Bruxelles. Les propriétaires souhaitaient moderniser entièrement leur espace de vie tout en conservant le charme de l'ancien.

Notre équipe a pris en charge l'ensemble des travaux : démolition des cloisons existantes pour créer une cuisine ouverte sur le salon, pose d'un nouveau parquet chêne, plafonnage intégral, peinture et installation d'une nouvelle salle de bain.

Le résultat : un appartement lumineux, contemporain et fonctionnel, livré dans les délais et le budget convenus.`,

    works: [
      "Démolition et évacuation",
      "Création cuisine ouverte",
      "Plafonnage murs et plafonds",
      "Pose parquet chêne contrecollé",
      "Peinture complète (2 couches)",
      "Rénovation salle de bain",
      "Installation électrique aux normes",
      "Finitions et nettoyage"
    ],
    
    gallery: [
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600",
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600",
      "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=600",
      "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=600"
    ],
    
    testimonial: {
      name: "Pierre et Sophie",
      text: "Nous sommes ravis du résultat. L'équipe a été professionnelle du début à la fin, le chantier est resté propre et les délais ont été respectés. Notre appartement est méconnaissable !",
      rating: 5
    },
    
    relatedService: "renovation-interieure-bruxelles"
  },
  
  "salle-de-bain-waterloo": {
    slug: "salle-de-bain-waterloo",
    title: "Salle de Bain Moderne Waterloo",
    subtitle: "Création d'une salle de bain contemporaine",
    metaTitle: "Rénovation Salle de Bain Waterloo | Perfect Team Renov",
    metaDescription: "Projet de rénovation salle de bain à Waterloo : douche italienne, carrelage grand format, meuble vasque sur mesure. Photos avant/après.",
    category: "Salle de Bain",
    location: "Waterloo",
    year: "2024",
    duration: "3 semaines",
    surface: "8 m²",
    
    heroImage: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200",
    beforeImage: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1200",
    afterImage: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200",
    
    description: `Transformation complète d'une salle de bain vieillissante en un **espace bien-être moderne** à Waterloo. Les propriétaires souhaitaient remplacer leur baignoire par une douche à l'italienne et moderniser l'ensemble.

Nous avons réalisé la dépose complète de l'ancienne salle de bain, créé une douche à l'italienne avec receveur extra-plat, posé un carrelage grand format effet béton ciré et installé un meuble vasque suspendu sur mesure.

L'éclairage LED intégré et la robinetterie encastrée apportent une touche design à cet espace de 8m².`,

    works: [
      "Dépose complète ancienne salle de bain",
      "Création douche italienne",
      "Étanchéité SPEC garantie",
      "Carrelage grand format 60x120",
      "Meuble vasque suspendu",
      "Robinetterie encastrée",
      "Éclairage LED",
      "Joints silicone et finitions"
    ],
    
    gallery: [
      "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=600",
      "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600",
      "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=600"
    ],
    
    testimonial: {
      name: "Marie",
      text: "Ma salle de bain ressemble maintenant à celle d'un hôtel de luxe ! L'équipe a géré tous les corps de métier, je n'ai eu qu'un seul interlocuteur. Parfait.",
      rating: 5
    },
    
    relatedService: "salle-de-bain-bruxelles"
  },
  
  "carrelage-uccle": {
    slug: "carrelage-uccle",
    title: "Carrelage Grand Format Uccle",
    subtitle: "Pose de carrelage 80x80 dans un appartement",
    metaTitle: "Pose Carrelage Grand Format Uccle | Perfect Team Renov",
    metaDescription: "Pose de carrelage grand format 80x80 à Uccle : salon, cuisine ouverte, couloir. Travail de précision, joints fins. Photos avant/après.",
    category: "Carrelage",
    location: "Uccle",
    year: "2024",
    duration: "2 semaines",
    surface: "65 m²",
    
    heroImage: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=1200",
    beforeImage: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1200",
    afterImage: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=1200",
    
    description: `Projet de **pose de carrelage grand format** dans un appartement à Uccle. Le client souhaitait un sol moderne et facile d'entretien pour son salon et sa cuisine ouverte.

Nous avons réalisé le ragréage du sol existant, puis posé 65m² de carrelage 80x80 imitation pierre naturelle avec des joints fins de 2mm. La continuité parfaite entre le salon et la cuisine donne une impression d'espace.

Un travail de précision millimétrique pour un résultat élégant et intemporel.`,

    works: [
      "Dépose ancien revêtement",
      "Ragréage auto-lissant",
      "Pose carrelage 80x80",
      "Joints fins 2mm",
      "Découpes sur mesure",
      "Seuils de porte",
      "Plinthes assorties",
      "Nettoyage et protection"
    ],
    
    gallery: [
      "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=600",
      "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=600"
    ],
    
    testimonial: {
      name: "François",
      text: "Les carreleurs ont fait un travail remarquable. Les joints sont parfaitement alignés, le niveau est impeccable. Je recommande vivement.",
      rating: 5
    },
    
    relatedService: "carrelage-bruxelles"
  },
  
  "peinture-anderlecht": {
    slug: "peinture-anderlecht",
    title: "Peinture & Décoration Anderlecht",
    subtitle: "Rafraîchissement complet d'un appartement",
    metaTitle: "Peinture Intérieure Anderlecht | Perfect Team Renov",
    metaDescription: "Travaux de peinture intérieure à Anderlecht : murs, plafonds, boiseries. Préparation soignée, 2 couches, finition impeccable.",
    category: "Peinture",
    location: "Anderlecht",
    year: "2024",
    duration: "10 jours",
    surface: "85 m²",
    
    heroImage: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200",
    beforeImage: "https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?w=1200",
    afterImage: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200",
    
    description: `**Rafraîchissement complet** d'un appartement de 85m² à Anderlecht avant mise en location. Le propriétaire souhaitait des couleurs neutres et lumineuses pour plaire au plus grand nombre.

Notre équipe a réalisé la préparation complète des surfaces (rebouchage, ponçage, sous-couche), puis appliqué 2 couches de peinture mate sur les murs et satinée sur les boiseries.

Résultat : un appartement propre, lumineux et prêt à accueillir de nouveaux locataires.`,

    works: [
      "Protection sols et meubles",
      "Rebouchage fissures",
      "Ponçage et dépoussiérage",
      "Application sous-couche",
      "Peinture murs (2 couches)",
      "Peinture plafonds",
      "Laque boiseries et portes",
      "Nettoyage final"
    ],
    
    gallery: [
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600",
      "https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?w=600"
    ],
    
    testimonial: {
      name: "Jean-Marc",
      text: "Travail rapide et soigné. L'appartement a été loué en moins d'une semaine après les travaux. Les peintres ont laissé les lieux impeccables.",
      rating: 5
    },
    
    relatedService: "peinture-interieure-bruxelles"
  },
  
  "plafonnage-lasne": {
    slug: "plafonnage-lasne",
    title: "Plafonnage Villa Lasne",
    subtitle: "Enduits et finitions pour une villa de standing",
    metaTitle: "Plafonnage Villa Lasne | Perfect Team Renov",
    metaDescription: "Travaux de plafonnage dans une villa à Lasne : enduit de lissage, finition parfaite. Préparation idéale pour peinture haut de gamme.",
    category: "Plafonnage",
    location: "Lasne",
    year: "2024",
    duration: "3 semaines",
    surface: "200 m²",
    
    heroImage: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200",
    beforeImage: "https://images.unsplash.com/photo-1560185127-6a8c0c6c4e0a?w=1200",
    afterImage: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200",
    
    description: `Travaux de **plafonnage complet** dans une villa neuve de 200m² à Lasne. Les propriétaires exigeaient une finition parfaitement lisse pour accueillir une peinture haut de gamme.

Nos plafonneurs ont appliqué un enduit de lissage sur l'ensemble des murs et plafonds, avec une attention particulière aux angles et aux jonctions. Trois passages ont été nécessaires pour atteindre le niveau de finition souhaité.

Le résultat : des surfaces parfaitement lisses, prêtes à recevoir la peinture.`,

    works: [
      "Préparation des supports",
      "Application première couche",
      "Ponçage intermédiaire",
      "Deuxième couche de lissage",
      "Finition angles et jonctions",
      "Troisième passe finition",
      "Ponçage final grain fin",
      "Dépoussiérage complet"
    ],
    
    gallery: [
      "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=600",
      "https://images.unsplash.com/photo-1560185127-6a8c0c6c4e0a?w=600"
    ],
    
    testimonial: {
      name: "Christine",
      text: "Un travail d'artisan ! Les murs sont parfaitement lisses, on ne voit aucun défaut. L'équipe est très professionnelle et le chantier est resté propre.",
      rating: 5
    },
    
    relatedService: "plafonnage-bruxelles"
  },
  
  "parquet-woluwe": {
    slug: "parquet-woluwe",
    title: "Pose Parquet Massif Woluwe",
    subtitle: "Installation de parquet chêne dans une maison de maître",
    metaTitle: "Pose Parquet Chêne Woluwe-Saint-Pierre | Perfect Team Renov",
    metaDescription: "Pose de parquet chêne massif à Woluwe-Saint-Pierre : 150m², pose collée, vitrification. Maison de maître, finition haut de gamme.",
    category: "Pose de Sol",
    location: "Woluwe-Saint-Pierre",
    year: "2024",
    duration: "2 semaines",
    surface: "150 m²",
    
    heroImage: "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=1200",
    beforeImage: "https://images.unsplash.com/photo-1560448075-bb485b1d4e36?w=1200",
    afterImage: "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=1200",
    
    description: `Installation de **parquet chêne massif** dans une magnifique maison de maître à Woluwe-Saint-Pierre. Les propriétaires souhaitaient retrouver le cachet d'origine avec un parquet noble et durable.

Nous avons posé 150m² de parquet chêne massif première qualité en pose collée. Après une semaine d'acclimatation du bois, nos poseurs ont travaillé pièce par pièce avec une grande précision.

La finition vitrifiée mat apporte protection et élégance pour des décennies à venir.`,

    works: [
      "Préparation et ragréage sol",
      "Acclimatation parquet (7 jours)",
      "Pose collée parquet chêne",
      "Découpes sur mesure",
      "Ponçage en 3 passes",
      "Application vitrificateur",
      "Pose plinthes chêne",
      "Seuils de porte"
    ],
    
    gallery: [
      "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=600",
      "https://images.unsplash.com/photo-1560448075-bb485b1d4e36?w=600"
    ],
    
    testimonial: {
      name: "Alain et Françoise",
      text: "Le parquet est magnifique, il donne un cachet incroyable à notre maison. La pose est parfaite, aucun grincement. Un travail de professionnels.",
      rating: 5
    },
    
    relatedService: "pose-sol-bruxelles"
  }
};

// Map old IDs to new slugs for compatibility
const ID_TO_SLUG = {
  1: "renovation-appartement-bruxelles",
  2: "salle-de-bain-waterloo",
  3: "carrelage-uccle",
  4: "peinture-anderlecht",
  5: "plafonnage-lasne",
  6: "parquet-woluwe"
};

const ProjectDetailPage = () => {
  const { projectSlug } = useParams();
  
  // Handle both slug and ID for backwards compatibility
  const slug = ID_TO_SLUG[projectSlug] || projectSlug;
  const project = PROJECTS_DATA[slug];
  
  // Update page title and meta description for SEO
  useEffect(() => {
    if (project) {
      document.title = project.metaTitle;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', project.metaDescription);
      }
    }
  }, [project]);
  
  if (!project) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Projet non trouvé</h1>
          <Link to="/projets">
            <Button className="bg-[#B87333] hover:bg-[#9A5F2A] text-white">
              Voir tous nos projets
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const projectsList = Object.values(PROJECTS_DATA);
  const currentIndex = projectsList.findIndex(p => p.slug === project.slug);
  const prevProject = currentIndex > 0 ? projectsList[currentIndex - 1] : null;
  const nextProject = currentIndex < projectsList.length - 1 ? projectsList[currentIndex + 1] : null;

  return (
    <div data-testid="project-detail-page" className="pt-20">
      {/* Breadcrumb */}
      <div className="bg-[#0A0A0A] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-white/60 hover:text-white transition-colors">Accueil</Link>
            <ChevronRight className="text-white/40" size={16} />
            <Link to="/projets" className="text-white/60 hover:text-white transition-colors">Projets</Link>
            <ChevronRight className="text-white/40" size={16} />
            <span className="text-[#B87333]">{project.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-[#0A0A0A] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#B87333]/20 text-[#B87333] font-medium text-sm uppercase tracking-widest px-4 py-2 rounded-full mb-6">
                {project.category}
              </span>
              <h1 className="text-4xl sm:text-5xl font-['Playfair_Display'] font-bold text-white mb-4">
                {project.title}
              </h1>
              <p className="text-xl text-white/70 mb-8">
                {project.subtitle}
              </p>
              
              {/* Project Info */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white/5 rounded-xl p-4">
                  <MapPin className="text-[#B87333] mb-2" size={20} />
                  <p className="text-white/60 text-sm">Localisation</p>
                  <p className="text-white font-semibold">{project.location}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <Ruler className="text-[#B87333] mb-2" size={20} />
                  <p className="text-white/60 text-sm">Surface</p>
                  <p className="text-white font-semibold">{project.surface}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <Clock className="text-[#B87333] mb-2" size={20} />
                  <p className="text-white/60 text-sm">Durée</p>
                  <p className="text-white font-semibold">{project.duration}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <Calendar className="text-[#B87333] mb-2" size={20} />
                  <p className="text-white/60 text-sm">Année</p>
                  <p className="text-white font-semibold">{project.year}</p>
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-wrap gap-4">
                <Link to="/devis">
                  <Button className="bg-[#B87333] hover:bg-[#9A5F2A] text-white px-6 py-6 rounded-full">
                    Devis Gratuit
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <a href="tel:+32488467522">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 py-6 rounded-full">
                    <Phone className="mr-2" size={18} />
                    +32 488 46 75 22
                  </Button>
                </a>
              </div>
            </div>
            
            {/* Before/After Slider */}
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <BeforeAfterSlider
                beforeImage={project.beforeImage}
                afterImage={project.afterImage}
                beforeLabel="Avant"
                afterLabel="Après"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#1A1A1A] mb-8">
            À propos de ce projet
          </h2>
          <div className="prose prose-lg max-w-none text-[#6B6B6B]">
            {project.description.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-4" dangerouslySetInnerHTML={{ 
                __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#1A1A1A]">$1</strong>') 
              }} />
            ))}
          </div>
        </div>
      </section>

      {/* Works Done */}
      <section className="py-16 bg-[#FAF9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#1A1A1A] mb-8">
            Travaux réalisés
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {project.works.map((work, index) => (
              <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm">
                <CheckCircle2 className="text-[#B87333] flex-shrink-0" size={20} />
                <span className="text-[#1A1A1A]">{work}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#1A1A1A] mb-8 text-center">
            Galerie photos
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {project.gallery.map((img, index) => (
              <div key={index} className="rounded-xl overflow-hidden shadow-lg">
                <LazyImage
                  src={img}
                  alt={`${project.title} - Photo ${index + 1}`}
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                  wrapperClassName="w-full h-48"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="inline-block bg-[#B87333]/20 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-4">
              Témoignage client
            </span>
          </div>
          
          <div className="bg-[#1A1A1A] rounded-3xl p-10 relative">
            <Quote className="absolute top-6 left-6 text-[#B87333]/30" size={48} />
            
            <div className="relative z-10">
              <div className="flex items-center gap-1 mb-6 justify-center">
                {[...Array(project.testimonial.rating)].map((_, i) => (
                  <Star key={i} className="text-[#E07B39] fill-[#E07B39]" size={20} />
                ))}
              </div>
              
              <p className="text-xl text-white/90 text-center leading-relaxed mb-8 font-light italic">
                "{project.testimonial.text}"
              </p>
              
              <div className="text-center">
                <p className="text-white font-semibold text-lg">{project.testimonial.name}</p>
                <p className="text-[#B87333]">{project.location}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#B87333] to-[#CD7F32]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold text-white mb-6">
            Vous avez un projet similaire ?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contactez-nous pour un devis gratuit et sans engagement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis">
              <Button className="bg-white text-[#B87333] hover:bg-white/90 px-8 py-6 rounded-full text-lg font-semibold">
                Demander un devis
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <a href="tel:+32488467522">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 rounded-full text-lg">
                <Phone className="mr-2" size={20} />
                +32 488 46 75 22
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-8 bg-[#FAF9F7] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {prevProject ? (
              <Link 
                to={`/projets/${prevProject.slug}`}
                className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#B87333] transition-colors"
              >
                <ArrowLeft size={20} />
                <span className="hidden sm:inline">{prevProject.title}</span>
                <span className="sm:hidden">Précédent</span>
              </Link>
            ) : <div />}
            
            <Link 
              to="/projets"
              className="text-[#B87333] hover:text-[#9A5F2A] font-medium"
            >
              Tous les projets
            </Link>
            
            {nextProject ? (
              <Link 
                to={`/projets/${nextProject.slug}`}
                className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#B87333] transition-colors"
              >
                <span className="hidden sm:inline">{nextProject.title}</span>
                <span className="sm:hidden">Suivant</span>
                <ArrowRight size={20} />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetailPage;
